import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Code2,
  Cpu,
  Database,
  Layers,
  Settings,
  Shield,
  Zap,
  Brain,
  Workflow,
  HelpCircle,
} from 'lucide-react';
import { useState } from 'react';

import SEO from '../components/SEO';

const KIToolEntwicklungPage = () => (
  <div className="pt-20">
    <SEO
      title="Individuelle KI-Tool Entwicklung – Chatbots, Datenanalyse & Automatisierung"
      description="Maßgeschneiderte KI-Anwendungen für Ihr Unternehmen: Chatbots, Datenanalyse, Workflow-Automatisierung. DSGVO-konform, made in Germany. LAIzyones Magdeburg."
      path="/ki-tool-entwicklung"
      keywords="KI Tool Entwicklung, KI Chatbot entwickeln, individuelle KI Anwendung, KI Datenanalyse, Workflow Automatisierung KI, KI Agenten, KI Software Entwicklung Deutschland, maßgeschneiderte KI Lösung"
      breadcrumbs={[
        { name: 'Startseite', url: '/' },
        { name: 'KI-Tool Entwicklung' },
      ]}
      serviceSchema={{
        name: 'Individuelle KI-Tool Entwicklung',
        description: 'Maßgeschneiderte KI-Anwendungen: Chatbots, Datenanalyse-Tools, Workflow-Automatisierung und individuelle KI-Agenten für Unternehmen.',
      }}
      faqSchema={[
        { question: 'Was kostet die Entwicklung eines individuellen KI-Tools?', answer: 'Die Kosten richten sich nach Komplexität und Umfang. Einfache KI-Chatbots starten ab wenigen tausend Euro, komplexe Systeme können mehr kosten. In der kostenlosen Erstberatung erstellen wir ein transparentes Angebot.' },
        { question: 'Brauche ich technische Vorkenntnisse?', answer: 'Nein. Wir übernehmen die komplette Entwicklung, Integration und Schulung. Sie beschreiben uns Ihre Prozesse und Anforderungen — wir kümmern uns um die technische Umsetzung.' },
        { question: 'Kann das KI-Tool in meine bestehende Software integriert werden?', answer: 'Ja. Wir entwickeln Schnittstellen (APIs) zu Ihren bestehenden Systemen wie CRM, ERP, Buchhaltung oder E-Mail. Die Integration ist nahtlos und erfordert keine Änderungen an Ihrer bestehenden IT.' },
        { question: 'Wie werden meine Daten geschützt?', answer: 'Alle Daten werden in Deutschland verarbeitet und gespeichert. Wir setzen auf verschlüsselte Verbindungen, Zugangsbeschränkungen und regelmäßige Sicherheitsaudits. DSGVO-Konformität ist bei uns Standard.' },
      ]}
    />
    <HeroSection />
    <ServicesSection />
    <TechStackSection />
    <ExamplesSection />
    <FAQSection />
    <CTASection />
  </div>
);

const HeroSection = () => (
  <section className="section bg-gray-50 relative overflow-hidden">
    <div className="absolute inset-0 grid-pattern" />
    <div className="container-custom relative z-10">
      <div className="max-w-4xl mx-auto text-center">
        <div className="badge mb-6 fade-up">Individuelle KI-Entwicklung</div>
        <div className="reveal-container">
          <h1 className="heading-xl text-4xl md:text-5xl lg:text-6xl text-gray-900 reveal-text mb-6">
            Individuelle <span className="gradient-text">KI-Tool Entwicklung</span> für Ihr Unternehmen
          </h1>
        </div>
        <p className="body-text text-base md:text-lg text-gray-600 max-w-3xl mx-auto fade-up mb-8">
          Wir entwickeln maßgeschneiderte KI-Anwendungen, die exakt auf Ihre Geschäftsprozesse
          zugeschnitten sind — keine Standardlösung, sondern Ihr individuelles KI-Tool.
          Aus Magdeburg und Braunschweig, für Unternehmen in ganz Deutschland.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center fade-up">
          <Link to="/#kontakt" className="btn-primary text-base">
            <span>Projekt besprechen</span>
            <ArrowRight className="w-5 h-5 relative z-10" />
          </Link>
          <Link to="/prozessanalyse" className="btn-secondary text-base inline-flex items-center justify-center gap-2">
            Erst Prozessanalyse
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const ServicesSection = () => {
  const services = [
    { icon: Brain, title: 'KI-Chatbots & Assistenten', desc: 'Intelligente Chatbots für Kundenservice, interne Wissensdatenbanken und automatisierte Beratung — trainiert auf Ihren Unternehmensdaten.' },
    { icon: Database, title: 'Datenanalyse-Tools', desc: 'Individuelle Dashboards und Analysesysteme, die Ihre Geschäftsdaten in Echtzeit auswerten und actionable Insights liefern.' },
    { icon: Workflow, title: 'Workflow-Automatisierung', desc: 'KI-gesteuerte Prozesstools, die Ihre spezifischen Arbeitsabläufe automatisieren und Systeme intelligent verknüpfen.' },
    { icon: Code2, title: 'API-Integrationen', desc: 'Nahtlose Anbindung von KI-Funktionen an Ihre bestehende Software — CRM, ERP, Buchhaltung und weitere Systeme.' },
    { icon: Layers, title: 'Dokumenten-KI', desc: 'Automatische Verarbeitung, Klassifizierung und Datenextraktion aus Dokumenten aller Art — Rechnungen, Verträge, Formulare.' },
    { icon: Settings, title: 'Individuelle KI-Agenten', desc: 'Autonome KI-Agenten, die komplexe Aufgabenketten eigenständig bearbeiten — von der Recherche bis zur Ergebnisaufbereitung.' },
  ];

  return (
    <section className="section bg-white">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="badge mb-4 fade-up">Unsere Leistungen</div>
          <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 mb-4 fade-up">
            Was wir für Sie <span className="gradient-text">entwickeln</span>
          </h2>
          <p className="body-text text-base md:text-lg text-gray-600 fade-up">
            Von einfachen Automatisierungen bis hin zu komplexen KI-Systemen — wir entwickeln genau die Lösung, die Ihr Unternehmen braucht.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="card group animate-card" data-testid={`service-${i}`}>
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-cyan-100 rounded-xl flex items-center justify-center mb-5 group-hover:from-emerald-200 group-hover:to-cyan-200 transition-colors">
                  <Icon className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-['Space_Grotesk'] font-semibold text-lg text-gray-900 mb-2">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const TechStackSection = () => {
  const features = [
    'Neueste GPT- und LLM-Modelle',
    'Computer Vision & Bilderkennung',
    'Natural Language Processing (NLP)',
    'Spracherkennung & Sprachsynthese',
    'Predictive Analytics & ML',
    'Sichere Cloud- oder On-Premise-Lösung',
  ];

  return (
    <section className="section bg-gray-50">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="badge mb-4 fade-up">Technologie</div>
            <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 mb-6 fade-up">
              State-of-the-Art <span className="gradient-text">KI-Technologie</span>
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-8 fade-up">
              Wir setzen auf die neuesten und leistungsfähigsten KI-Modelle und Frameworks,
              um für Ihr Unternehmen die bestmögliche Lösung zu entwickeln — immer DSGVO-konform
              und mit höchsten Sicherheitsstandards.
            </p>
            <div className="space-y-3 fade-up">
              {features.map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span className="text-gray-700 font-medium text-sm">{f}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="fade-up">
            <div className="tech-card p-8">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/20 mb-4">
                  <Cpu className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="font-['Space_Grotesk'] font-bold text-2xl text-white mb-2">Made in Germany</h3>
              </div>
              <p className="text-gray-400 text-center text-base leading-relaxed">
                Alle unsere KI-Tools werden in Deutschland entwickelt und gehostet.
                Ihre Daten verlassen nie den europäischen Rechtsraum — <span className="text-emerald-400 font-semibold">garantiert DSGVO-konform</span>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ExamplesSection = () => {
  const examples = [
    { title: 'KI-Chatbot für Versicherungsmakler', desc: 'Automatische Beantwortung von Kundenanfragen, Schadenmeldungen und Tarifvergleiche — 24/7 verfügbar, trainiert auf Versicherungswissen.', metrics: '60% weniger Support-Aufwand' },
    { title: 'Angebots-Generator für Handwerksbetrieb', desc: 'KI erstellt automatisch kalkulierte Angebote aus Kundenanfragen, inklusive Materialliste und Zeitplanung.', metrics: '3x schnellere Angebotsstellung' },
    { title: 'Qualitätskontrolle in der Fertigung', desc: 'Computer Vision erkennt Produktfehler in Echtzeit auf dem Fließband — zuverlässiger und schneller als manuelle Prüfung.', metrics: '95% Fehlererkennungsrate' },
  ];

  return (
    <section className="section bg-white">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="badge mb-4 fade-up">Projektbeispiele</div>
          <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 mb-4 fade-up">
            Individuelle KI-Lösungen <span className="gradient-text">in der Praxis</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {examples.map((e, i) => (
            <div key={i} className="card animate-card">
              <h3 className="font-['Space_Grotesk'] font-semibold text-lg text-gray-900 mb-3">{e.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">{e.desc}</p>
              <div className="flex items-center gap-2 text-emerald-600 font-semibold text-sm">
                <Zap className="w-4 h-4" />
                {e.metrics}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const faqs = [
    { q: 'Was kostet die Entwicklung eines individuellen KI-Tools?', a: 'Die Kosten richten sich nach Komplexität und Umfang. Einfache KI-Chatbots starten ab wenigen tausend Euro, komplexe Systeme können mehr kosten. In der kostenlosen Erstberatung erstellen wir ein transparentes Angebot.' },
    { q: 'Brauche ich technische Vorkenntnisse?', a: 'Nein. Wir übernehmen die komplette Entwicklung, Integration und Schulung. Sie beschreiben uns Ihre Prozesse und Anforderungen — wir kümmern uns um die technische Umsetzung.' },
    { q: 'Kann das KI-Tool in meine bestehende Software integriert werden?', a: 'Ja. Wir entwickeln Schnittstellen (APIs) zu Ihren bestehenden Systemen wie CRM, ERP, Buchhaltung oder E-Mail. Die Integration ist nahtlos und erfordert keine Änderungen an Ihrer bestehenden IT.' },
    { q: 'Wie werden meine Daten geschützt?', a: 'Alle Daten werden in Deutschland verarbeitet und gespeichert. Wir setzen auf verschlüsselte Verbindungen, Zugangsbeschränkungen und regelmäßige Sicherheitsaudits. DSGVO-Konformität ist bei uns Standard, nicht Option.' },
  ];

  return (
    <section className="section bg-gray-50">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="badge mb-4 fade-up"><HelpCircle className="w-3.5 h-3.5" /> Häufige Fragen</div>
          <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 mb-4 fade-up">
            FAQ zur <span className="gradient-text">KI-Tool-Entwicklung</span>
          </h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="card overflow-hidden">
              <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left group">
                <h3 className="font-['Space_Grotesk'] font-semibold text-base text-gray-900 pr-4 group-hover:text-emerald-600 transition-colors">{faq.q}</h3>
                <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-180 text-emerald-500' : ''}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${openIndex === i ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}>
                <p className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTASection = () => (
  <section className="section bg-white pb-24 md:pb-32">
    <div className="container-custom">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0A0F14] to-[#111820] p-8 md:p-12 lg:p-16">
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <div className="relative z-10 text-center max-w-3xl mx-auto">
          <h2 className="font-['Space_Grotesk'] font-bold text-3xl md:text-4xl text-white mb-6">
            Ihre Idee, unser <span className="text-emerald-400">KI-Know-how</span>
          </h2>
          <p className="text-gray-400 text-base md:text-lg mb-8">
            Lassen Sie uns gemeinsam Ihr individuelles KI-Tool entwickeln.
            Kostenloses Erstgespräch — wir beraten Sie ehrlich und unverbindlich.
          </p>
          <Link to="/#kontakt" className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold px-8 py-4 rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all">
            Projekt besprechen <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

export default KIToolEntwicklungPage;
